import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, Signal } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { AppState } from '@fcom/core';
import { CmsContent, CmsImageData, CmsTarget, CmsTaxonomy } from '@fcom/core-api';
import { AspectRatioString } from '@fcom/common/interfaces/images.interface';
import { WindowRef } from '@fcom/core/providers';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';
import { LoginActions } from '@fcom/core/actions';
import { LoginStep } from '@fcom/core-api/login';
import { Amount } from '@fcom/dapi';
import { MediaQueryService } from '@fcom/common/services';
import { JoinService } from '@fcom/common/login/services/join.service';

import { ButtonMode, ButtonTheme } from '../buttons';
import { PromoTheme } from './enum';
import { TagTheme } from '../tag';
import { ButtonState } from '../../../common-booking/modules/ancillaries/interfaces';
import { getFontLevel, getHeaderLevel } from '../../utils/taxonomy-utils';
import { IconPosition } from '../icons';

export interface PromoCardCtaData extends CmsTarget {
  icon?: { name: SvgLibraryIcon; position: IconPosition };
  status?: string;
  theme?: ButtonTheme;
}

@Component({
  selector: 'fcom-promo-card',
  templateUrl: './promo-card.component.html',
  styleUrls: ['./promo-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    '[class.double]': 'isDouble()',
    '[class.full-width]': 'isFullWidth()',
    '[class.special-grid-size-for-laptop]': 'specialGridSizeForLaptop()',
  },
})
export class PromoCardComponent implements OnInit {
  private router = inject(Router);
  private windowRef = inject(WindowRef);
  private gtmService = inject(GtmService);
  private store$ = inject<Store<AppState>>(Store);
  private mediaQueryService = inject(MediaQueryService);
  private joinService = inject(JoinService);

  readonly ButtonTheme = ButtonTheme;
  readonly IconLibrary = IconLibrary;
  readonly PromoTheme = PromoTheme;
  readonly ButtonMode = ButtonMode;
  readonly TagTheme = TagTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ButtonState = ButtonState;
  readonly DEFAULT_HEADER_LEVEL = 3;

  readonly theme = input<PromoTheme>(PromoTheme.WHITE);
  readonly data = input<Partial<CmsContent>>();
  readonly imageData = input<CmsImageData | null>(null);
  readonly ctaData = input<PromoCardCtaData | null>(null);
  readonly specialGridSizeForLaptop = input(false);
  readonly aspectRatio = input<AspectRatioString>('21x9');
  readonly isTextOnTop = input(false);
  readonly fullWidth = input(false);
  readonly analyticsEventName = input<string>();
  readonly isProductPromo = input(false);
  readonly pricePrefix = input<string>();
  readonly price = input<Amount>();
  readonly originalPrice = input<Amount>();
  readonly aviosPrice = input<string>();

  readonly buttonCta = output<void>();

  readonly isDouble = computed(() => Boolean(this.data()) && Boolean(this.imageData()));
  readonly isFullWidth = computed(() => this.isDouble() && this.fullWidth());

  isMobile: Signal<boolean>;

  ngOnInit(): void {
    this.isMobile = this.mediaQueryService.isBreakPoint('mobile');
  }

  onCardClick(): void {
    if (this.isProductPromo()) {
      this.buttonCta.emit();
    } else {
      const cta = this.ctaData();
      if (cta) {
        this.navigateToUrl(cta);
      }
    }
  }

  navigateToUrl(cta: CmsTarget): void {
    if (this.analyticsEventName()) {
      this.gtmService.trackElement(
        this.analyticsEventName(),
        GaContext.NO_CONTEXT,
        ElementTypes.BUTTON,
        cta.url,
        ElementActions.CLICK
      );
    }

    if (cta?.url?.includes('auth.finnair.com')) {
      if (cta?.url?.includes('login')) {
        this.openLoginDialog();
      } else if (cta?.url?.includes('join')) {
        this.openJoinDialog();
      }
    } else if (cta?.url && !cta?.openInNewTab) {
      this.router.navigateByUrl(cta?.url);
    } else if (cta?.url && cta?.openInNewTab) {
      this.windowRef.nativeWindow.open(cta?.url, '_blank');
    }
  }

  openLoginDialog(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
  }

  openJoinDialog(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.joinService.joinDialogOpen$.next(true);
  }

  headerLevel(tags: CmsTaxonomy[]): number | undefined {
    return getHeaderLevel(tags);
  }

  headerFontLevel(tags: CmsTaxonomy[]): string | undefined {
    const theming = this.theme() !== PromoTheme.WHITE ? 'mr-clear-y white-text text-icon-reversed' : 'mr-clear-y';

    if (this.isProductPromo()) {
      return `${theming} font-heading-5 font-title-small`;
    }

    return `${theming} ${getFontLevel(tags) ?? ''}`;
  }
}
